import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { Popover, PopoverContent, PopoverPortal } from "@radix-ui/react-popover"
import { useState } from "react"


import { Message } from "@/components/Chat/Message.tsx"
import { MessageActionsPopup } from "@/components/Chat/MessageActionsPopup.tsx"
import { useObserveRefCallback } from "@/hooks/useObserveRefCallback.ts"
import { useScrollIntoViewEffect } from "@/hooks/useScrollIntoViewEffect.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { UTaskProcessDetails } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GitHubVersionInfo } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"
import type { IObservableValue } from "mobx"

export const MessageContainerWithActionsPopup = (props: {
  observer: IntersectionObserver
  message: ChatMessageModel
  isAgentRunning: IObservableValue<boolean>
  currentVersionCommitId: IObservableValue<string | null>
  successfulVersionCommitIds: IObservableValue<Set<string>>
  gitHubVersionInfo: IObservableValue<GitHubVersionInfo | null>
  runningProcess: UTaskProcessDetails | null
  onVersionCheckout: (commitId: string) => Promise<void>
  onMarkSuccessful: (commitId: string, successful: boolean) => Promise<void>
}) => {
  const observeRefCallback = useObserveRefCallback(props.observer)
  const [hovered, setHovered] = useState(false)
  const [popupHovered, setPopupHovered] = useState(false)

  useScrollIntoViewEffect(props.message.focusId, {
    block: "center",
    behavior: "instant"
  })

  return (
    <Popover open={(hovered || popupHovered) && !props.message.editingModel}>
      <MessageDataContainerMarker
        ref={observeRefCallback}
        data-message-id={props.message.id}
        data-focus-id={props.message.focusId}
        data-testid="chat-message"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Message
          message={props.message}
          isAgentRunning={props.isAgentRunning}
          currentVersionCommitId={props.currentVersionCommitId}
          successfulVersionCommitIds={props.successfulVersionCommitIds}
          gitHubVersionInfo={props.gitHubVersionInfo}
          runningProcess={props.runningProcess}
          onVersionCheckout={props.onVersionCheckout}
          onMarkSuccessful={props.onMarkSuccessful}
        />
      </MessageDataContainerMarker>
      <PopoverPortal>
        <PopoverContent
          className={props.message.isMyMessage ? MyMessageAnimation : OtherMessageAnimation}
          align={props.message.isMyMessage ? "end" : "start"}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={() => setPopupHovered(true)}
          onMouseLeave={() => setPopupHovered(false)}
          data-testid="chat-message-actions-menu"
        >
          <MessageActionsPopup model={props.message} />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}

const MessageDataContainerMarker = styled.div``

const MyMessageAnimation = css`
  transform-origin: var(--radix-popover-content-transform-origin);
  animation: action-popup-my-message 0.15s ease-in-out;

  @keyframes action-popup-my-message {
    from {
      opacity: 0;
      transform: scale(0.8);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const OtherMessageAnimation = css`
  transform-origin: var(--radix-popover-content-transform-origin);
  animation: action-popup-other-message 0.15s ease-in-out;

  @keyframes action-popup-other-message {
    from {
      opacity: 0;
      transform: scale(0.8);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`